import { RUNTIME_SOURCE } from '@/canvas-runtime/runtime';
import { CSP_CONTENT, CSP_META, injectCsp } from '@/lib/csp';

export { CSP_CONTENT, CSP_META, injectCsp };

const RUNTIME_TAG = `<script id="cdc-runtime">${RUNTIME_SOURCE}</script>`;

const HEAD_OPEN = /<head\b[^>]*>/i;
const HTML_OPEN = /<html\b[^>]*>/i;
const DOCTYPE = /<!doctype[^>]*>/i;

/** Inject the runtime script *after* CSP so the meta tag is the first head child. */
export function injectRuntime(html: string): string {
  if (HEAD_OPEN.test(html)) {
    return html.replace(HEAD_OPEN, (m) => `${m}${RUNTIME_TAG}`);
  }
  return html.replace(HTML_OPEN, (m) => `${m}<head>${RUNTIME_TAG}</head>`);
}

export function buildArtifactHtml(rawHtml: string): string {
  const withDoctype = DOCTYPE.test(rawHtml) ? rawHtml : `<!doctype html>${rawHtml}`;
  return injectRuntime(injectCsp(withDoctype));
}

/**
 * Minimal HTML used as the iframe `srcdoc` *during* a streaming generation.
 *
 * We mount the runtime + Tailwind CDN once at stream start, then push body chunks via
 * `postMessage({type:'patch-append'})`. Inline `<style>` tags in chunks apply on insertion;
 * `<script>` tags inserted via `insertAdjacentHTML` do *not* execute (HTML5 parser rule),
 * which is why one final clean reload happens at `message_stop` to run any in-artifact JS.
 */
export const STREAMING_SCAFFOLD = `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>Generating…</title>
    <script src="https://cdn.tailwindcss.com"></script>
    <style>
      :root { color-scheme: light dark; }
      html, body { background: #fafafa; }
      @media (prefers-color-scheme: dark) { html, body { background: #0a0a0a; } }
      body { font-family: ui-sans-serif, system-ui, sans-serif; margin: 0; }
      body:empty::before {
        content: '';
        display: block;
        margin: 1.5rem;
        height: 1.5rem;
        max-width: 14rem;
        border-radius: 0.5rem;
        background: linear-gradient(90deg, rgba(148,163,184,.18) 0%, rgba(148,163,184,.4) 50%, rgba(148,163,184,.18) 100%);
        background-size: 200% 100%;
        animation: cdc-shimmer 1.4s ease-in-out infinite;
      }
      @keyframes cdc-shimmer {
        0%   { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
      /* Cosmetic: any structural element appearing during streaming gets a subtle fade-in */
      body > * { animation: cdc-fade-in 220ms ease-out; }
      @keyframes cdc-fade-in {
        from { opacity: 0; transform: translateY(2px); }
        to   { opacity: 1; transform: translateY(0); }
      }
    </style>
  </head>
  <body></body>
</html>`;

export const HELLO_WORLD_ARTIFACT = `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>CDC · Hello</title>
    <style>
      :root { color-scheme: light dark; }
      body { font-family: ui-sans-serif, system-ui, sans-serif; padding: 2rem; }
      h1 { font-size: 1.5rem; margin: 0 0 .5rem; }
      p  { color: #525252; line-height: 1.5; max-width: 32rem; }
    </style>
  </head>
  <body>
    <main>
      <h1>Canvas is live</h1>
      <p>This artifact is rendered inside a sandboxed iframe with a CSP that blocks outbound network calls. Streamed designs replace this on first send.</p>
    </main>
  </body>
</html>`;
